import { useEffect, useState } from 'react'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { useAutoScroll } from 'utils/hooks'
import {
    addCabana,
    updateCabana,
    cleanCabanas,
    setEditCabana,
    CabanaItemType,
    DEFAULT_QUANTITY,
} from 'store/cabana-slice'
import { useCabana } from 'store/cabana-slice/hooks'
import { useApp } from 'store/steps'
import { Wrapper, Creator, OriginalCabana } from './styles'
import AddItem from 'components/shared/AddItem'
import { CabanaQuantity } from 'routes/Upsell/CabanasCreator/fields/CabanaQuantity'
import { CabanaSummary } from './fields/CabanaSummary'
import { FormattedMessage } from 'react-intl'
import Loader from 'components/shared/Loader'
import { CABANAS_LIMIT } from 'constants/limits'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import { CabanaDate } from './fields/CabanaDate'
import { CabanaRequirementsWarning } from './fields/CabanaRequirementsWarning'
import OrderedCabana from './fields/OrderedCabana'
import { DefaultTheme } from 'styled-components'
import CabanaItem from 'components/shared/Reservation/ticketRows/CabanaItem'
import TicketRow from 'components/shared/Reservation/ticketRows/TicketRow'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'

interface CabanaCreatorProps {
    hasAltEditMode?: boolean
    theme?: DefaultTheme
}

const CabanaCreator = ({
    hasAltEditMode = false,
    theme,
}: CabanaCreatorProps) => {
    const dispatch = useAppDispatch()
    const { isCreatorMode } = useApp()
    const { isDarkThemeForCabanas } = usePromotions()

    const [currentCabanaIndex, setCurrentCabanaIndex] = useState(0)
    const [blockingError] = useState<'calendar' | 'dates' | 'holidays' | null>(
        null
    )
    const [isCabanaDataFetched] = useState(true)
    const [cabanasLimit, setCabanasLimit] = useState<number>(CABANAS_LIMIT)
    const [isActionInProgress, setIsActionInProgress] = useState(false)

    const cartTickets = useAppSelector((state) => state.cart.items.tickets)
    const selfcareTickets = useAppSelector((state) => state.selfcare.tickets)
    const areInitialDataFetched =
        isCabanaDataFetched && blockingError !== 'calendar'

    const { cabanas, defaultUnitPrice, edited } = useAppSelector(
        (state) => state.cabana
    )

    const { removeCabanaFromCart } = useCabana(currentCabanaIndex)
    const { calculateScroll, animatePendingScroll, scrollIndex } =
        useAutoScroll()

    let scrollTimeout: ReturnType<typeof setTimeout>

    const hasNoTickets = isCreatorMode
        ? !cartTickets.length
        : !selfcareTickets?.length ||
          selfcareTickets?.every((ticket) => ticket.changeType === 'return')
    const hasNoMatchingTickets = isCreatorMode
        ? cartTickets.some((ticket) => ticket.detail.zone === 'jamango')
        : selfcareTickets?.some((ticket) => {
              const ticketState = ticket.changes || ticket.original
              return ticketState.detail.zone === 'jamango'
          })

    useEffect(
        () => () => {
            dispatch(cleanCabanas())
            clearTimeout(scrollTimeout)
        },
        []
    )

    useEffect(() => {
        animatePendingScroll(`cabana-${cabanas.length - 1}`)
        animatePendingScroll(`cabana-${currentCabanaIndex}`)
        animatePendingScroll('cabanas')
    }, [cabanas.length, scrollIndex])

    useEffect(() => {
        setCabanasLimit(CABANAS_LIMIT)
    }, [currentCabanaIndex])

    const handleSetDate = (cabanaIndex: number, value: string) => {
        dispatch(
            updateCabana({
                index: cabanaIndex,
                date: value,
            })
        )
    }

    const handleSetCabanasQuantity = (
        cabanaIndex: number,
        quantity: number
    ) => {
        dispatch(
            updateCabana({
                index: cabanaIndex,
                quantity,
            })
        )
    }

    const handleAddCabana = () => {
        dispatch(addCabana({}))
        calculateScroll({
            trigerringSection: 'cabanas',
            trigerringAction: 'add',
            index: cabanas.length,
        })
        setCurrentCabanaIndex(cabanas.length)
    }

    const handleEdit = (cabanaIndex: number) => {
        setCurrentCabanaIndex(cabanaIndex)
        dispatch(
            setEditCabana({
                index: cabanaIndex,
                editState: 'editing',
            })
        )
        calculateScroll({
            trigerringSection: 'cabanas',
            trigerringAction: 'edit',
            index: cabanaIndex,
        })
    }

    let originQuantity = 0
    let originDate = ''

    if (edited) {
        const {
            quantity,
            detail: { date },
        } = edited.origin.cabana

        originQuantity = quantity || 0
        originDate = date || ''
    }

    const handleRemove = () => {
        setIsActionInProgress(true)
        removeCabanaFromCart().then(() => {
            setIsActionInProgress(false)
        })
    }

    return (
        <Wrapper gap={16} mt={[16, 24]} isFullWidth id="cabanas" theme={theme}>
            {hasAltEditMode && (
                <Text variant="bodyLarge" color="grayscale_900">
                    {cabanas.length ? (
                        <FormattedMessage
                            id="selfcare.cabana.caption_added_to_order"
                            defaultMessage="Dodano do zamówienia"
                        />
                    ) : (
                        <FormattedMessage
                            id="selfcare.cabana.caption_add_to_order"
                            defaultMessage="Wybierz dodatkowe Cabany"
                        />
                    )}
                </Text>
            )}
            {/* TODO: move into standalone component/s */}
            {cabanas.map((cabana, index) => {
                const {
                    cabana: {
                        cartItemId,
                        quantity,
                        detail: { date, boxId },
                    },
                    isSubmitted,
                } = cabana
                const isEdited = !!edited && edited.index === index
                const cabanasQuantity = quantity || DEFAULT_QUANTITY
                const calculatedLimit =
                    isEdited && originDate === date
                        ? originQuantity + cabanasLimit
                        : cabanasLimit
                const hasLimitError = cabanasQuantity > calculatedLimit
                const isModified =
                    originDate !== date || originQuantity !== quantity
                const cabanaProductData = {
                    detail: {
                        date: originDate,
                        orderCabanaId: cartItemId || '',
                        boxId,
                    },
                    price: defaultUnitPrice,
                    quantity: cabanasQuantity,
                }
                const cabanaReservationData = {
                    original: { ...cabanaProductData },
                    changes: { ...cabanaProductData },
                }

                if (isSubmitted && !isEdited)
                    return (
                        <OrderedCabana
                            id={`cabana-${index}`}
                            key={`ordered${cartItemId}`}
                            cabanaIndex={index}
                            cabanasQuantity={cabanasQuantity}
                            unitPrice={defaultUnitPrice}
                            editHandler={() => handleEdit(index)}
                            theme={theme}
                        />
                    )

                return (
                    <Creator
                        isFullWidth
                        pl={[16, 40]}
                        pr={[16, 40]}
                        pt={[24, 40]}
                        pb={[24, 40]}
                        gap={[32, 40]}
                        key={cartItemId}
                        id={`cabana-${index}`}
                        theme={theme}
                    >
                        {hasAltEditMode && isEdited && (
                            <OriginalCabana
                                isFullWidth
                                gap={[16, 40]}
                                pb={[16, 32]}
                                mb={[6, 0]}
                                direction={['column', 'row']}
                                align={['flex-start', 'center']}
                            >
                                <TicketRow
                                    price={defaultUnitPrice * originQuantity}
                                >
                                    <Text
                                        variant="bodySmall"
                                        color="grayscale_700"
                                        mr={8}
                                    >
                                        {originQuantity}x
                                    </Text>
                                    <CabanaItem
                                        cabana={cabanaReservationData}
                                    />
                                </TicketRow>
                                <Box
                                    direction={['row-reverse', 'row']}
                                    gap={16}
                                >
                                    {isActionInProgress ? (
                                        <Loader hasNoShrink />
                                    ) : (
                                        <Text
                                            as={'button'}
                                            variant="bodyStrong"
                                            color={'grayscale_700'}
                                            onClick={handleRemove}
                                        >
                                            <FormattedMessage
                                                id="global.btn.remove"
                                                defaultMessage="Usuń"
                                            />
                                        </Text>
                                    )}
                                </Box>
                            </OriginalCabana>
                        )}
                        {blockingError === 'calendar' && <Loader />}
                        {areInitialDataFetched && (
                            <>
                                <CabanaDate
                                    cabanasQuantity={cabanasQuantity}
                                    date={date}
                                    onCalendarOpen={() => null}
                                    onDateChange={(date) =>
                                        handleSetDate(index, date)
                                    }
                                    onLimitChange={setCabanasLimit}
                                    isError={false}
                                    isConflict={false}
                                    errorMsg={null}
                                    isFetching={false}
                                    theme={theme}
                                    cabanaLimit={calculatedLimit}
                                    index={index}
                                />

                                <CabanaQuantity
                                    quantity={cabanasQuantity}
                                    setCabanaQuantity={(quantity: number) =>
                                        handleSetCabanasQuantity(
                                            index,
                                            quantity
                                        )
                                    }
                                    theme={theme}
                                    limit={calculatedLimit}
                                    index={index}
                                />

                                {(hasNoTickets || hasNoMatchingTickets) && (
                                    <CabanaRequirementsWarning
                                        theme={theme}
                                        hasNoMatchingTickets={
                                            !!hasNoMatchingTickets
                                        }
                                    />
                                )}

                                <CabanaSummary
                                    cabanaIndex={index}
                                    isEdited={isEdited}
                                    isCtaDisabled={
                                        hasLimitError ||
                                        !!blockingError ||
                                        !isModified
                                    }
                                    setBookingConflict={() => null}
                                    hasAltEditMode={hasAltEditMode && isEdited}
                                    originTotalPrice={
                                        originQuantity * defaultUnitPrice
                                    }
                                    theme={theme}
                                />
                            </>
                        )}
                    </Creator>
                )
            })}
            {cabanas.every((route: CabanaItemType) => route.isSubmitted) &&
                !edited && (
                    <AddItem
                        onClick={handleAddCabana}
                        theme={theme}
                        shouldKeepBorderInDarkMode={isDarkThemeForCabanas}
                        testId="cabana-button-add-another"
                    >
                        <FormattedMessage
                            id="global.btn.add_another_cabana"
                            defaultMessage="Dodaj kolejną Cabanę"
                        />
                    </AddItem>
                )}
        </Wrapper>
    )
}

export default CabanaCreator
